import { Camera, ChefHat, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ALLERGENS, type Allergen } from "../../../shared/constants";
import { parseMissing } from "../../../shared/food-search-ro";
import type { FoodDto } from "../../../shared/foods";
import { NUTRIENTS } from "../../../shared/nutrition";
import type { RecipeDto } from "../../../shared/recipes";
import { ApiRequestError, api as request } from "../../api/client";
import { foodApi } from "../../api/queries";
import { CHIPS, StatusChip } from "../../components/kit";
import { useToast } from "../../components/Toast";
import { Alert, ICON } from "../../components/ui";
import { BaseRecipeEditor } from "../nutrition/BaseRecipeEditor";
import { FoodSearch } from "./FoodSearch";
import { LabelModal, NUTRIENT_LABELS } from "./LabelModal";

// "Din ce e făcut" inside the product editor's "Ingrediente și alergeni" section (food database, phase 3;
// docs/BAZA_INGREDIENTE.md): the restaurant declares the ingredients and their grams, the app computes the values per
// 100 g, the allergens, the E numbers and "from a frozen product" from the database. Nothing here is published yet,
// and the allergens are only proposed: the restaurant still ticks and confirms them above.

interface Line {
  /** Stable key of the row while it is edited. */
  key: string;
  kind: "food" | "product" | "base";
  id: string;
  name: string;
  brand: string | null;
  grams: string;
  needsLabel: boolean;
}

const allergenLabel = (code: Allergen) => ALLERGENS.find(([c]) => c === code)?.[1] ?? code;
const fmt = (n: number | null) => (n === null ? "—" : String(n).replace(".", ","));

export function RecipeBlock({
  rid,
  mid,
  pid,
  onUseAllergens,
  onSaved,
  onBaseSaved,
  saveLabel = "Salvează rețeta și calculează",
  compact = false,
  shareMissing = false,
}: {
  rid: string;
  mid: string;
  pid: string | null;
  /** In the product editor: ticks the recipe's allergens in the form above. */
  onUseAllergens?: (allergens: Allergen[]) => void;
  onSaved?: () => void;
  /** A base preparation was confirmed: other products of the menu may have been completed with it. */
  onBaseSaved?: () => void;
  saveLabel?: string;
  /** Inside the setup wizard: without the long explanation. */
  compact?: boolean;
  /** An ingredient chosen for a missing one is also added to the menu's other products that miss it. */
  shareMissing?: boolean;
}) {
  const toast = useToast();
  const api = useMemo(() => foodApi(rid), [rid]);
  const [lines, setLines] = useState<Line[]>([]);
  const [cooked, setCooked] = useState("");
  const [recipe, setRecipe] = useState<RecipeDto | null>(null);
  const [query, setQuery] = useState("");
  // Ingredients the AI proposed but did not find: each is chosen from the database (or a label) or removed.
  const [missing, setMissing] = useState<string[]>([]);
  const [target, setTarget] = useState<number | null>(null);
  const [preparing, setPreparing] = useState<number | null>(null);
  const [label, setLabel] = useState<{ replace?: number; missing?: number; name?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!pid) return;
    let alive = true;
    api
      .recipe(mid, pid)
      .then((r) => {
        if (!alive) return;
        setRecipe(r);
        setCooked(r.cookedGrams ? String(r.cookedGrams) : "");
        setLines(r.lines.map((l) => ({ ...l, key: crypto.randomUUID(), grams: String(l.grams) })));
        setMissing(r.missing);
      })
      .catch(() => alive && setError("Rețeta nu a putut fi încărcată."));
    return () => {
      alive = false;
    };
  }, [api, mid, pid]);

  if (!pid)
    return <p className="muted caption">Salvează produsul întâi; apoi poți declara din ce e făcut.</p>;

  /** Adds a food; `forMissing` takes the grams the AI proposed and marks that ingredient as done. */
  const add = (f: FoodDto, at?: number, forMissing: number | null = null) => {
    const proposed = forMissing === null ? null : parseMissing(missing[forMissing] ?? "").grams;
    const line: Line = {
      key: crypto.randomUUID(),
      kind: f.kind,
      id: f.id,
      name: f.name,
      brand: f.brand,
      grams: proposed ? String(proposed).replace(".", ",") : "100",
      needsLabel: f.needsLabel,
    };
    setLines((ls) =>
      at === undefined ? [...ls, line] : ls.map((l, i) => (i === at ? { ...line, grams: l.grams } : l)),
    );
    if (forMissing !== null) {
      const ingredient = parseMissing(missing[forMissing] ?? "").name;
      setMissing((m) => m.filter((_, i) => i !== forMissing));
      // One choice for the whole menu (a base preparation is already applied everywhere when it is saved).
      if (shareMissing && f.kind !== "base" && ingredient)
        void request<{ updated: number }>(
          "POST",
          `/restaurants/${rid}/nutrition/menus/${mid}/missing/resolve`,
          {
            ingredient,
            kind: f.kind,
            id: f.id,
            except: pid,
          },
        )
          .then((r) => {
            if (r.updated)
              toast(
                `„${ingredient}” a fost completat și la ${r.updated === 1 ? "încă un produs" : `alte ${r.updated} produse`}.`,
              );
            onBaseSaved?.();
          })
          .catch(() => undefined);
    }
    setTarget(null);
    setQuery("");
  };

  async function save() {
    if (!pid) return;
    setError(null);
    if (missing.length) {
      setError(
        `Mai ai ${missing.length === 1 ? "un ingredient" : `${missing.length} ingrediente`} de completat: alege-l din bază, fotografiază eticheta sau scoate-l.`,
      );
      return;
    }
    const parsed = lines.map((l) => ({ kind: l.kind, id: l.id, grams: Number(l.grams.replace(",", ".")) }));
    if (parsed.some((l) => !(l.grams > 0 && l.grams <= 5000))) {
      setError("Fiecare ingredient are nevoie de un gramaj între 1 și 5000 g.");
      return;
    }
    const c = cooked.trim() ? Number(cooked.replace(",", ".")) : null;
    if (c !== null && !(c > 0)) {
      setError("Gramajul servit trebuie să fie un număr pozitiv.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.saveRecipe(mid, pid, { lines: parsed, cookedGrams: c });
      setRecipe(r);
      setLines(r.lines.map((l) => ({ ...l, key: crypto.randomUUID(), grams: String(l.grams) })));
      setMissing(r.missing);
      onSaved?.();
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : "Rețeta nu a putut fi salvată.");
    } finally {
      setSaving(false);
    }
  }

  const n = recipe?.nutrition ?? null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 12 }}>
      <div hidden={compact}>
        <strong style={{ fontWeight: 600 }}>Din ce e făcut</strong>
        <p className="muted caption" style={{ margin: "2px 0 0" }}>
          Adaugă ingredientele și gramajele. Calculăm valorile nutriționale la 100 g, alergenii, E-urile și
          dacă produsul e din ingrediente congelate, din datele etichetelor și ale tabelelor oficiale.
          Salvarea rețetei de mână contează ca verificată de tine; cu „Valori nutriționale” activ, valorile
          apar pe meniu ca estimate.
        </p>
      </div>
      {recipe?.aiUnverified && (
        <div>
          <StatusChip {...CHIPS.aiUnverified} />
        </div>
      )}
      {missing.length > 0 && (
        <Alert tone="warning" title="De completat">
          AI-ul a propus aceste ingrediente, dar nu le-a găsit în baza de date. Pentru fiecare: alege-l din
          bază (păstrăm gramajul propus), spune din ce e făcut dacă e un preparat (blat, sos, aluat),
          fotografiază eticheta dacă e cumpărat, sau scoate-l.
          <ul style={{ listStyle: "none", margin: "8px 0 0", padding: 0, display: "grid", gap: 6 }}>
            {missing.map((m, i) => {
              const { name, grams } = parseMissing(m);
              return (
                <li key={m} className="row" style={{ gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ flex: "1 1 160px", fontWeight: target === i ? 600 : undefined }}>
                    {name}
                    {grams ? <span className="muted"> · {String(grams).replace(".", ",")} g</span> : null}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setTarget(i);
                      setQuery(name);
                    }}
                  >
                    <Search {...ICON} aria-hidden="true" /> Alege din bază
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    aria-expanded={preparing === i}
                    onClick={() => setPreparing(preparing === i ? null : i)}
                  >
                    <ChefHat {...ICON} aria-hidden="true" /> E un preparat
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setLabel({ missing: i, name })}
                  >
                    <Camera {...ICON} aria-hidden="true" /> Etichetă
                  </button>
                  {preparing === i && (
                    <div style={{ flexBasis: "100%" }}>
                      <BaseRecipeEditor
                        rid={rid}
                        name={name}
                        onCancel={() => setPreparing(null)}
                        onDone={(r) => {
                          setPreparing(null);
                          add(
                            {
                              kind: "base",
                              id: r.id,
                              name: r.name,
                              brand: null,
                              quantity: null,
                              ean: null,
                              source: "base",
                              allergens: r.allergens,
                              traces: [],
                              additives: [],
                              frozen: false,
                              per100: r.per100,
                              needsLabel: false,
                              declaredHere: true,
                            },
                            undefined,
                            i,
                          );
                          onBaseSaved?.();
                        }}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    aria-label={`Scoate ${name}`}
                    onClick={() => {
                      setMissing((ms) => ms.filter((_, j) => j !== i));
                      setTarget(null);
                    }}
                  >
                    <Trash2 {...ICON} aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        </Alert>
      )}
      {error && <Alert tone="warning">{error}</Alert>}
      {lines.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {lines.map((l, i) => (
            <li key={l.key} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ flex: "1 1 160px" }}>
                {l.name}
                {l.brand ? <span className="muted"> · {l.brand}</span> : null}
              </span>
              {l.needsLabel && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setLabel({ replace: i, name: l.name })}
                  title="Datele acestui produs nu sunt verificate: fotografiază eticheta lui"
                >
                  <Camera {...ICON} aria-hidden="true" /> Verifică eticheta
                </button>
              )}
              {/* Grams, unit and remove stay together on a phone. */}
              <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  className="field-input"
                  style={{ width: 84 }}
                  inputMode="decimal"
                  aria-label={`Gramaj ${l.name}`}
                  value={l.grams}
                  onChange={(e) =>
                    setLines((ls) => ls.map((x, j) => (j === i ? { ...x, grams: e.target.value } : x)))
                  }
                />
                <span className="muted">g</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  aria-label={`Scoate ${l.name}`}
                  onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                >
                  <Trash2 {...ICON} aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="stack" style={{ gap: 6 }}>
        <FoodSearch
          rid={rid}
          id={`rcp-search-${pid}`}
          label={
            target !== null ? `Alege: ${parseMissing(missing[target] ?? "").name}` : "Adaugă un ingredient"
          }
          query={query}
          onQuery={setQuery}
          onPick={(f) => add(f, undefined, target)}
          autoFocus={target !== null}
        />
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setLabel({ name: query.trim() || undefined })}
        >
          <Camera {...ICON} aria-hidden="true" /> Nu găsești produsul? Fotografiază eticheta
        </button>
      </div>
      <div className="field">
        <label className="field-label" htmlFor={`rcp-cooked-${pid}`}>
          Gramaj servit, după gătire (opțional)
        </label>
        <input
          id={`rcp-cooked-${pid}`}
          className="field-input"
          style={{ width: 120 }}
          inputMode="decimal"
          value={cooked}
          onChange={(e) => setCooked(e.target.value)}
        />
      </div>
      <div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={save} disabled={saving}>
          {saving ? "Calculez…" : saveLabel}
        </button>
      </div>
      {n && (
        <div className="card" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <strong style={{ fontWeight: 600 }}>Valori estimate, calculate din rețeta declarată</strong>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }} />
                <th style={{ textAlign: "right" }}>la 100 g</th>
                <th style={{ textAlign: "right" }}>porție ({fmt(n.grams)} g)</th>
              </tr>
            </thead>
            <tbody>
              {NUTRIENTS.map((k) => (
                <tr key={k}>
                  <td>
                    {NUTRIENT_LABELS[k]}
                    {n.incomplete.includes(k) ? <span className="muted"> (incomplet)</span> : null}
                  </td>
                  <td style={{ textAlign: "right" }}>{fmt(n.per100g[k])}</td>
                  <td style={{ textAlign: "right" }}>{fmt(n.perPortion[k])}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ margin: 0 }}>
            <strong style={{ fontWeight: 600 }}>Alergeni din rețetă:</strong>{" "}
            {n.allergens.length
              ? n.allergens.map(allergenLabel).join(", ")
              : "niciunul în datele ingredientelor"}
            {n.traces.length ? ` · poate conține urme de ${n.traces.map(allergenLabel).join(", ")}` : ""}
          </p>
          <p style={{ margin: 0 }}>
            <strong style={{ fontWeight: 600 }}>E-uri:</strong>{" "}
            {n.additives.length ? n.additives.join(", ") : "niciunul"}
            {n.frozen ? " · conține ingrediente congelate" : ""}
          </p>
          {n.allergens.length > 0 && onUseAllergens && (
            <div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => onUseAllergens(n.allergens)}
              >
                Bifează mai sus alergenii din rețetă
              </button>
              <p className="muted caption" style={{ margin: "2px 0 0" }}>
                Verifică-i și confirmă-i tu: lipsa unui alergen din datele unui produs nu înseamnă că nu îl
                conține.
              </p>
            </div>
          )}
        </div>
      )}
      {label && (
        <LabelModal
          rid={rid}
          suggestedName={label.name}
          onClose={() => setLabel(null)}
          onSaved={(p) => {
            add(p, label.replace, label.missing ?? null);
            setLabel(null);
          }}
        />
      )}
    </div>
  );
}
